/**
 * `tree` is the single source of truth for navigation, CMS content and page metadata.
 * `gatsby-node.js` walks it to create one page per leaf node; `pages.js` also derives
 * `pages` (flat list, see compilePages) and `PATH_URLS` (route constants, see buildPathUrls)
 * from it — don't hand-maintain those separately, just add/edit nodes here.
 *
 * Each flat entry in `pages` (and therefore each page's `pageContext.pageData` at runtime)
 * carries everything a component needs to render itself without ever inspecting the URL:
 * `path`, `tab` (parent tab key), `displayname`, `headline`, `content`, `additional_info`,
 * `notes`, `seoTitle`, `stories`, `meta` — plus `filters` (nav breadcrumbs), added separately
 * in `gatsby-node.js` since it depends on sibling nodes rather than the node itself.
 *
 * Shape (top to bottom):
 *   tab        { key, categories: [category, ...] }
 *   category   leaf  : { key, displayname, headline, content, additional_info, notes?,
 *                        subcategories: [], seoTitle, stories, meta }  → becomes a routable page
 *              group : { key, displayname, subcategories: [subcategory, ...] } → pure nav
 *                        grouping (no content fields), not a page itself
 *   subcategory       always a leaf, same shape as a leaf category above
 *
 * Field reference (leaf nodes only, i.e. nodes with `meta` that become a routable page):
 *   key                           URL segment, e.g. 'flex-score'; also mechanically upper-cased/
 *                                 underscored to derive the matching PATH_URLS constant name
 *   displayname                   label shown in navigation/filters
 *   headline                      page title shown above the content text
 *   content                       intro paragraph shown on the page
 *   additional_info.sourcesHref   link to the sources page section, or null
 *   additional_info.text          source citation shown below the table; may contain
 *                                 '{{month}}'/'{{year}}' placeholders, resolved via
 *                                 .monthOverrides/.yearOverrides (keyed by year, 'default' fallback)
 *   notes                         optional array of footnote strings rendered below the table
 *   seoTitle                      <title>/meta title for the page
 *   stories                       array of story objects for a page's teaser section, referenced via a
 *                                 named group from `stories.js` (e.g. `storyGroups.flexCheck`)
 *   meta.years                    optional year tabs for this page; when set, replaces the API timeline
 *                                 (omit to use the backend timeline as-is)
 *   meta.regionFilters            which region-filter tabs to show (null = none); indices into meta.tableHead
 *   meta.tableHead                column headers per region index, e.g. { region: { 0: [...], 1: [...] } }
 *   meta.tableHead_<year>         optional override of meta.tableHead for one specific year (e.g. tableHead_2025)
 *   meta.data.page                backend dataset id used to build the table-data fetch URL (see useDataOrchestrator.js)
 *   meta.data.category            optional sub-path/query segment of the fetch URL, e.g. 'energyChange/solarRoof'
 *   meta.data.categoryTooltip     category id passed to the map tooltip renderer (see MapDrawTooltipHelper.js)
 *   meta.api.tooltip              backend endpoint for map tooltip data
 *   meta.api.ranksStates          backend endpoint for state-level rankings
 *   meta.api.ranksLandkreise      backend endpoint for district-level rankings (omit if not available)
 *   meta.api.geosRegion2          optional static GeoJSON asset path (e.g. ladestationen), used instead of an API call
 */

const { storyGroups } = require('./stories');

const tree = [
  {
    key: 'flex-check',
    categories: [
      {
        key: 'flex-score',
        displayname: 'Flex-Score',
        headline: 'Flexibilitäts-Potenziale',
        content: 'In welchen Regionen sind die deutschen Haushalte im Hinblick auf das Energiesystem besonders „flexibel“? Der Überblick.',
        additional_info: {
          sourcesHref: null,
          text: 'Auswertung von E.ON und der Forschungsstelle für Energiewirtschaft (FfE) unter anderem auf Basis von Modellierungen und Simulationen, externen Daten und einer repräsentativen YouGov-Umfrage, 2026'
        },
        notes: ['Ergebniswerte sind gerundet. Die Reihenfolge der Platzierungen ergibt sich aus der Berechnung mit ungerundeten Werten. Ein gleich-gerundeter Ergebniswert führt deshalb nicht zwangsläufig zu einer gleichen Platzierung.'],
        subcategories: [],
        seoTitle: 'Flexibilitäts-Check - Flex-Score',
        stories: storyGroups.flexCheck,
        meta: {
          regionFilters: null,
          tableHead: { region: { 0: ['Region', 'Platz', 'Flex-Score'] } },
          data: { category: 'flexScore', categoryTooltip: 'flexScore', page: 'flexCheck' },
          api: { tooltip: '/flexCheck/tooltip', ranksStates: '/flexCheck/rank/states/flexScore' }
        }
      },
      {
        key: 'technologie',
        displayname: 'Technologie',
        subcategories: [
          {
            key: 'gesamt',
            displayname: 'Gesamt',
            headline: 'Flexibilitäts-Potenziale',
            content: 'Welche Energiemengen lassen sich durch Wärmepumpen, (bidirektionale) E-Autos, Heimspeicher und Mikro-Flexibilitäten jährlich potenziell verschieben? Der Überblick.',
            additional_info: {
              sourcesHref: null,
              text: 'Auswertung von E.ON und der Forschungsstelle für Energiewirtschaft (FfE) unter anderem auf Basis von Modellierungen und Simulationen, externen Daten und einer repräsentativen YouGov-Umfrage, 2026'
            },
            notes: ['Ergebniswerte sind gerundet. Die Reihenfolge der Platzierungen ergibt sich aus der Berechnung mit ungerundeten Werten. Ein gleich-gerundeter Ergebniswert führt deshalb nicht zwangsläufig zu einer gleichen Platzierung.'],
            seoTitle: 'Flexibilitäts-Check - Technologie - Gesamt',
            stories: storyGroups.flexCheck,
            meta: {
              regionFilters: null,
              tableHead: { region: { 0: ['Region', 'Platz', 'Gesamt kWh/Haushalt'] } },
              data: { category: 'technologyTotal', categoryTooltip: 'technologyTotal', page: 'flexCheck' },
              api: { tooltip: '/flexCheck/tooltip', ranksStates: '/flexCheck/rank/states/technologyTotal' }
            }
          },
          {
            key: 'waermepumpen',
            displayname: 'Wärmepumpen',
            headline: 'Flexibilitäts-Potenziale',
            content: 'Welche Energiemengen lassen sich durch Wärmepumpen jährlich potenziell verschieben? Der Überblick.',
            additional_info: {
              sourcesHref: null,
              text: 'Auswertung von E.ON und der Forschungsstelle für Energiewirtschaft (FfE) unter anderem auf Basis von Modellierungen und Simulationen, externen Daten und einer repräsentativen YouGov-Umfrage, 2026'
            },
            notes: ['Ergebniswerte sind gerundet. Die Reihenfolge der Platzierungen ergibt sich aus der Berechnung mit ungerundeten Werten. Ein gleich-gerundeter Ergebniswert führt deshalb nicht zwangsläufig zu einer gleichen Platzierung.'],
            seoTitle: 'Flexibilitäts-Check - Technologie - Wärmepumpen',
            stories: storyGroups.flexCheck,
            meta: {
              regionFilters: null,
              tableHead: { region: { 0: ['Region', 'Platz', 'Wärmepumpen kWh/Haushalt'] } },
              data: { category: 'technologyHeatpumps', categoryTooltip: 'technologyHeatpumps', page: 'flexCheck' },
              api: { tooltip: '/flexCheck/tooltip', ranksStates: '/flexCheck/rank/states/technologyHeatpumps' }
            }
          },
          {
            key: 'e-autos',
            displayname: 'E-Autos',
            headline: 'Flexibilitäts-Potenziale',
            content: 'Welche Energiemengen lassen sich durch (bidirektionale) E-Autos jährlich potenziell verschieben? Der Überblick.',
            additional_info: {
              sourcesHref: null,
              text: 'Auswertung von E.ON und der Forschungsstelle für Energiewirtschaft (FfE) unter anderem auf Basis von Modellierungen und Simulationen, externen Daten und einer repräsentativen YouGov-Umfrage, 2026'
            },
            notes: ['Ergebniswerte sind gerundet. Die Reihenfolge der Platzierungen ergibt sich aus der Berechnung mit ungerundeten Werten. Ein gleich-gerundeter Ergebniswert führt deshalb nicht zwangsläufig zu einer gleichen Platzierung.'],
            seoTitle: 'Flexibilitäts-Check - Technologie - E-Autos',
            stories: storyGroups.flexCheck,
            meta: {
              regionFilters: null,
              tableHead: { region: { 0: ['Region', 'Platz', 'E-Autos kWh/Haushalt'] } },
              data: { category: 'technologyElectricVehicles', categoryTooltip: 'technologyElectricVehicles', page: 'flexCheck' },
              api: { tooltip: '/flexCheck/tooltip', ranksStates: '/flexCheck/rank/states/technologyElectricVehicles' }
            }
          },
          {
            key: 'heimspeicher',
            displayname: 'Heimspeicher',
            headline: 'Flexibilitäts-Potenziale',
            content: 'Welche Energiemengen lassen sich durch Heimspeicher jährlich potenziell verschieben? Der Überblick.',
            additional_info: {
              sourcesHref: null,
              text: 'Auswertung von E.ON und der Forschungsstelle für Energiewirtschaft (FfE) unter anderem auf Basis von Modellierungen und Simulationen, externen Daten und einer repräsentativen YouGov-Umfrage, 2026'
            },
            notes: ['Ergebniswerte sind gerundet. Die Reihenfolge der Platzierungen ergibt sich aus der Berechnung mit ungerundeten Werten. Ein gleich-gerundeter Ergebniswert führt deshalb nicht zwangsläufig zu einer gleichen Platzierung.'],
            seoTitle: 'Flexibilitäts-Check - Technologie - Heimspeicher',
            stories: storyGroups.flexCheck,
            meta: {
              regionFilters: null,
              tableHead: { region: { 0: ['Region', 'Platz', 'Heimspeicher kWh/Haushalt'] } },
              data: { category: 'technologyHomeStorage', categoryTooltip: 'technologyHomeStorage', page: 'flexCheck' },
              api: { tooltip: '/flexCheck/tooltip', ranksStates: '/flexCheck/rank/states/technologyHomeStorage' }
            }
          },
          {
            key: 'mikro-flexibilitaeten',
            displayname: 'Mikro-Flexibilitäten',
            headline: 'Flexibilitäts-Potenziale',
            content: 'Welche Energiemengen lassen sich durch Waschmaschine, Trockner und Spülmaschine jährlich potenziell verschieben? Der Überblick.',
            additional_info: {
              sourcesHref: null,
              text: 'Auswertung von E.ON und der Forschungsstelle für Energiewirtschaft (FfE) unter anderem auf Basis von Modellierungen und Simulationen, externen Daten und einer repräsentativen YouGov-Umfrage, 2026'
            },
            notes: ['Ergebniswerte sind gerundet. Die Reihenfolge der Platzierungen ergibt sich aus der Berechnung mit ungerundeten Werten. Ein gleich-gerundeter Ergebniswert führt deshalb nicht zwangsläufig zu einer gleichen Platzierung.'],
            seoTitle: 'Flexibilitäts-Check - Technologie - Mikro-Flexibilitäten',
            stories: storyGroups.flexCheck,
            meta: {
              regionFilters: null,
              tableHead: { region: { 0: ['Region', 'Platz', 'Mikro kWh/Haushalt'] } },
              data: { category: 'technologyMicroFlexibilities', categoryTooltip: 'technologyMicroFlexibilities', page: 'flexCheck' },
              api: { tooltip: '/flexCheck/tooltip', ranksStates: '/flexCheck/rank/states/technologyMicroFlexibilities' }
            }
          }
        ]
      },
      {
        key: 'finanziell',
        displayname: 'Finanziell',
        headline: 'Flexibilitäts-Potenziale',
        content: 'Wie viel Geld verschenken wir jedes Jahr durch Unflexibilität in der Stromnutzung? Der Überblick.',
        additional_info: {
          sourcesHref: null,
          text: 'Auswertung von E.ON und der Forschungsstelle für Energiewirtschaft (FfE) unter anderem auf Basis von Modellierungen und Simulationen, externen Daten und einer repräsentativen YouGov-Umfrage, 2026'
        },
        notes: ['Ergebniswerte sind gerundet. Die Reihenfolge der Platzierungen ergibt sich aus der Berechnung mit ungerundeten Werten. Ein gleich-gerundeter Ergebniswert führt deshalb nicht zwangsläufig zu einer gleichen Platzierung.'],
        subcategories: [],
        seoTitle: 'Flexibilitäts-Check - Finanziell',
        stories: storyGroups.flexCheck,
        meta: {
          years: [2026],
          regionFilters: null,
          tableHead: { region: { 0: ['Region', 'Platz', 'Gesamtersparnis'] } },
          data: { category: 'financial', categoryTooltip: 'financial', page: 'flexCheck' },
          api: { tooltip: '/flexCheck/tooltip', ranksStates: '/flexCheck/rank/states/financial' }
        }
      },
      {
        key: 'wissen-und-bereitschaft',
        displayname: 'Wissen & Bereitschaft',
        subcategories: [
          {
            key: 'flex-wissen',
            displayname: 'Flex-Wissen',
            headline: 'Flexibilitäts-Potenziale',
            content: 'Wie gut sind die Menschen in Deutschland über Flexibilität im Energiesystem informiert? Der Überblick.',
            additional_info: {
              sourcesHref: null,
              text: 'Auswertung von E.ON und der Forschungsstelle für Energiewirtschaft (FfE) unter anderem auf Basis von Modellierungen und Simulationen, externen Daten und einer repräsentativen YouGov-Umfrage, 2026'
            },
            notes: ['Ergebniswerte sind gerundet. Die Reihenfolge der Platzierungen ergibt sich aus der Berechnung mit ungerundeten Werten. Ein gleich-gerundeter Ergebniswert führt deshalb nicht zwangsläufig zu einer gleichen Platzierung.'],
            seoTitle: 'Flexibilitäts-Check - Wissen & Bereitschaft - Flex-Wissen',
            stories: storyGroups.flexCheck,
            meta: {
              regionFilters: null,
              tableHead: { region: { 0: ['Region', 'Platz', 'Flex-Wissen'] } },
              data: { category: 'flexKnowledge', categoryTooltip: 'flexKnowledge', page: 'flexCheck' },
              api: { tooltip: '/flexCheck/tooltip', ranksStates: '/flexCheck/rank/states/flexKnowledge' }
            }
          },
          {
            key: 'flex-bereitschaft',
            displayname: 'Flex-Bereitschaft',
            headline: 'Flexibilitäts-Potenziale',
            content: 'Welche Bereitschaft zeigen die Menschen in Deutschland, Stromverbräuche zu verschieben? Der Überblick.*',
            additional_info: {
              sourcesHref: null,
              text: 'Auswertung von E.ON und der Forschungsstelle für Energiewirtschaft (FfE) unter anderem auf Basis von Modellierungen und Simulationen, externen Daten und einer repräsentativen YouGov-Umfrage, 2026'
            },
            notes: ['Ergebniswerte sind gerundet. Die Reihenfolge der Platzierungen ergibt sich aus der Berechnung mit ungerundeten Werten. Ein gleich-gerundeter Ergebniswert führt deshalb nicht zwangsläufig zu einer gleichen Platzierung.', '* Für die Flex-Bereitschaft in 2030 wurden die Teilnehmenden gefragt, ob sie in Zukunft stärker als bislang Stromverbräuche verschieben würden.'],
            seoTitle: 'Flexibilitäts-Check - Wissen & Bereitschaft - Flex-Bereitschaft',
            stories: storyGroups.flexCheck,
            meta: {
              regionFilters: null,
              tableHead: { region: { 0: ['Region', 'Platz', 'Flex-Bereitschaft'] } },
              data: { category: 'flexReadiness', categoryTooltip: 'flexReadiness', page: 'flexCheck' },
              api: { tooltip: '/flexCheck/tooltip', ranksStates: '/flexCheck/rank/states/flexReadiness' }
            }
          }
        ]
      }
    ]
  },
  {
    key: 'zukunftsindex',
    categories: [
      {
        key: 'co2-einsparung-jahr',
        displayname: 'CO₂-Einsparung/Jahr',
        headline: 'Kurzfristiges CO₂-Einsparpotenzial',
        content: 'Welches CO₂-Einsparpotenzial besteht u.a. in den Bereichen Wärmepumpe, Solar und Mobilität, wenn die Menschen in Deutschland ihre kurzfristigen Pläne umsetzen? Der Überblick.',
        additional_info: {
          sourcesHref: 'https://www.eon.de/de/unternehmen/presse/energieatlas/quellen.html#zukunftsindex',
          text: 'Berechnung von Energy Brainpool im Auftrag von E.ON auf Basis von Civey-Umfragen, 2023'
        },
        subcategories: [],
        seoTitle: 'Zukunftsindex - CO₂-Einsparung/Jahr (geplant)',
        stories: storyGroups.zukunftsindex,
        meta: {
          regionFilters: null,
          tableHead: { region: { 0: ['Region', 'Platz', 'CO₂-Einsparung/Jahr (geplant)'] } },
          data: { category: 'co2Savings', categoryTooltip: 'co2Savings', page: 'futureIndex' },
          api: { tooltip: '/futureIndex/tooltip', ranksStates: '/futureIndex/rank/states/co2Savings' }
        }
      },
      {
        key: 'durch-energiewende-zuhause',
        displayname: 'durch Energiewende Zuhause',
        subcategories: [
          {
            key: 'durch-energiewende-zuhause-gesamt',
            displayname: 'Gesamt',
            headline: 'Energiewende Zuhause',
            content: 'Welche CO₂-Einsparpotenziale sind im Bereich „Energiewende Zuhause“ möglich, wenn die Menschen in Deutschland ihre kurzfristigen Pläne umsetzen?',
            additional_info: {
              sourcesHref: 'https://www.eon.de/de/unternehmen/presse/energieatlas/quellen.html#zukunftsindex',
              text: 'Berechnung von Energy Brainpool im Auftrag von E.ON auf Basis von Civey-Umfragen, 2023'
            },
            seoTitle: 'Zukunftsindex - durch Energiewende Zuhause - Gesamt',
            stories: storyGroups.zukunftsindex,
            meta: {
              regionFilters: null,
              tableHead: { region: { 0: ['Region', 'Platz', 'durch Energiewende Zuhause'] } },
              data: { category: 'energyChange', categoryTooltip: 'energyChange', page: 'futureIndex', isTotal: true },
              api: { tooltip: '/futureIndex/tooltip', ranksStates: '/futureIndex/rank/states/energyChange' }
            }
          },
          {
            key: 'durch-energiewende-zuhause-installation-dach-solaranlage',
            displayname: 'Installation Dach-Solaranlage',
            headline: 'Energiewende Zuhause',
            content: 'Welche CO₂-Einsparpotenziale sind konkret durch die Installation von Dach-Solaranlagen möglich, wenn die Menschen in Deutschland ihre kurzfristigen Pläne umsetzen?',
            additional_info: {
              sourcesHref: 'https://www.eon.de/de/unternehmen/presse/energieatlas/quellen.html#zukunftsindex',
              text: 'Berechnung von Energy Brainpool im Auftrag von E.ON auf Basis von Civey-Umfragen, 2023'
            },
            seoTitle: '',
            stories: storyGroups.zukunftsindex,
            meta: {
              regionFilters: null,
              tableHead: { region: { 0: ['Region', 'Platz', 'Installation Dach-Solaranlage'] } },
              data: { category: 'energyChange/solarRoof', categoryTooltip: 'energyChange', page: 'futureIndex' },
              api: { tooltip: '/futureIndex/tooltip', ranksStates: '/futureIndex/rank/states/energyChange/solarRoof' }
            }
          },
          {
            key: 'durch-energiewende-zuhause-installation-balkon-solaranlage',
            displayname: 'Installation Balkon-Solaranlage',
            headline: 'Energiewende Zuhause',
            content: 'Welche CO₂-Einsparpotenziale sind konkret durch die Installation von Balkon-Solaranlagen möglich, wenn die Menschen in Deutschland ihre kurzfristigen Pläne umsetzen?',
            additional_info: {
              sourcesHref: 'https://www.eon.de/de/unternehmen/presse/energieatlas/quellen.html#zukunftsindex',
              text: 'Berechnung von Energy Brainpool im Auftrag von E.ON auf Basis von Civey-Umfragen, 2023'
            },
            seoTitle: '',
            stories: storyGroups.zukunftsindex,
            meta: {
              regionFilters: null,
              tableHead: { region: { 0: ['Region', 'Platz', 'Installation Balkon-Solaranlage'] } },
              data: { category: 'energyChange/solarBalcony', categoryTooltip: 'energyChange', page: 'futureIndex' },
              api: { tooltip: '/futureIndex/tooltip', ranksStates: '/futureIndex/rank/states/energyChange/solarBalcony' }
            }
          },
          {
            key: 'durch-energiewende-zuhause-effiziente-heizanlage',
            displayname: 'Effiziente Heizanlage',
            headline: 'Energiewende Zuhause',
            content: 'Welche CO₂-Einsparpotenziale sind konkret durch die Installation von Wärmepumpen möglich, wenn die Menschen in Deutschland ihre kurzfristigen Pläne umsetzen?',
            additional_info: {
              sourcesHref: 'https://www.eon.de/de/unternehmen/presse/energieatlas/quellen.html#zukunftsindex',
              text: 'Berechnung von Energy Brainpool im Auftrag von E.ON auf Basis von Civey-Umfragen, 2023'
            },
            seoTitle: '',
            stories: storyGroups.zukunftsindex,
            meta: {
              regionFilters: null,
              tableHead: { region: { 0: ['Region', 'Platz', 'Effiziente Heizanlage'] } },
              data: { category: 'energyChange/efficientHeating', categoryTooltip: 'energyChange', page: 'futureIndex' },
              api: { tooltip: '/futureIndex/tooltip', ranksStates: '/futureIndex/rank/states/energyChange/efficientHeating' }
            }
          }
        ]
      },
      {
        key: 'durch-verkehrswende',
        displayname: 'durch Verkehrswende',
        subcategories: [
          {
            key: 'durch-verkehrswende-gesamt',
            displayname: 'Gesamt',
            headline: 'Verkehrswende',
            content: 'Welche CO₂-Einsparpotenziale sind im Bereich „Verkehrswende“ möglich, wenn die Menschen in Deutschland ihre kurzfristigen Pläne umsetzen?',
            additional_info: {
              sourcesHref: 'https://www.eon.de/de/unternehmen/presse/energieatlas/quellen.html#zukunftsindex',
              text: 'Berechnung von Energy Brainpool im Auftrag von E.ON auf Basis von Civey-Umfragen, 2023'
            },
            seoTitle: 'Zukunftsindex - durch Verkehrswende',
            stories: storyGroups.zukunftsindex,
            meta: {
              regionFilters: null,
              tableHead: { region: { 0: ['Region', 'Platz', 'durch Verkehrswende'] } },
              data: { category: 'transportChange', categoryTooltip: 'transportChange', page: 'futureIndex', isTotal: true },
              api: { tooltip: '/futureIndex/tooltip', ranksStates: '/futureIndex/rank/states/transportChange' }
            }
          },
          {
            key: 'durch-verkehrswende-umstieg-e-auto',
            displayname: 'Umstieg E-Auto',
            headline: 'Verkehrswende',
            content: 'Welche CO₂-Einsparpotenziale sind konkret durch den Umstieg auf Elektrofahrzeuge möglich, wenn die Menschen in Deutschland ihre kurzfristigen Pläne umsetzen?',
            additional_info: {
              sourcesHref: 'https://www.eon.de/de/unternehmen/presse/energieatlas/quellen.html#zukunftsindex',
              text: 'Berechnung von Energy Brainpool im Auftrag von E.ON auf Basis von Civey-Umfragen, 2023'
            },
            seoTitle: 'Zukunftsindex - durch Verkehrswende',
            stories: storyGroups.zukunftsindex,
            meta: {
              regionFilters: null,
              tableHead: { region: { 0: ['Region', 'Platz', 'Umstieg E-Auto'] } },
              data: { category: 'transportChange/electronicVehicle', categoryTooltip: 'transportChange', page: 'futureIndex' },
              api: { tooltip: '/futureIndex/tooltip', ranksStates: '/futureIndex/rank/states/transportChange/electronicVehicle' }
            }
          },
          {
            key: 'durch-verkehrswende-umstieg-oepnv',
            displayname: 'Umstieg ÖPNV',
            headline: 'Verkehrswende',
            content: 'Welche CO₂-Einsparpotenziale sind konkret durch den Umstieg auf ÖPNV möglich, wenn die Menschen in Deutschland ihre kurzfristigen Pläne umsetzen?',
            additional_info: {
              sourcesHref: 'https://www.eon.de/de/unternehmen/presse/energieatlas/quellen.html#zukunftsindex',
              text: 'Berechnung von Energy Brainpool im Auftrag von E.ON auf Basis von Civey-Umfragen, 2023'
            },
            seoTitle: 'Zukunftsindex - durch Verkehrswende',
            stories: storyGroups.zukunftsindex,
            meta: {
              regionFilters: null,
              tableHead: { region: { 0: ['Region', 'Platz', 'Umstieg ÖPNV'] } },
              data: { category: 'transportChange/publicTransport', categoryTooltip: 'transportChange', page: 'futureIndex' },
              api: { tooltip: '/futureIndex/tooltip', ranksStates: '/futureIndex/rank/states/transportChange/publicTransport' }
            }
          },
          {
            key: 'durch-verkehrswende-bidirektionales-laden',
            displayname: 'Bidirektionales Laden',
            headline: 'Verkehrswende',
            content: 'Welche CO₂-Einsparpotenziale sind langfristig gesehen durch bidirektionales Laden möglich?',
            additional_info: {
              sourcesHref: 'https://www.eon.de/de/unternehmen/presse/energieatlas/quellen.html#zukunftsindex',
              text: 'Berechnung von Energy Brainpool im Auftrag von E.ON auf Basis von Civey-Umfragen, 2023'
            },
            notes: ['* Bidirektionales Laden steht kurzfristig noch nicht flächendeckend zur Verfügung, deshalb ist kein Wert für das kommende Jahr darstellbar.'],
            seoTitle: 'Zukunftsindex - durch Verkehrswende',
            stories: storyGroups.zukunftsindex,
            meta: {
              regionFilters: null,
              tableHead: { region: { 0: ['Region', 'Platz', 'Bidirektionales Laden*'] } },
              data: { category: 'transportChange/bidirectionalCharging', categoryTooltip: 'transportChange', page: 'futureIndex' },
              api: { tooltip: '/futureIndex/tooltip', ranksStates: '/futureIndex/rank/states/transportChange/bidirectionalCharging' }
            }
          }
        ]
      },
      {
        key: 'durch-verbrauchswende',
        displayname: 'durch Verbrauchswende',
        subcategories: [
          {
            key: 'durch-verbrauchswende-gesamt',
            displayname: 'Gesamt',
            headline: 'Verbrauchswende',
            content: 'Welche CO₂-Einsparpotenziale sind im Bereich „Verbrauchswende“ insgesamt möglich, wenn die Menschen in Deutschland ihre kurzfristigen Pläne umsetzen?',
            additional_info: {
              sourcesHref: 'https://www.eon.de/de/unternehmen/presse/energieatlas/quellen.html#zukunftsindex',
              text: 'Berechnung von Energy Brainpool im Auftrag von E.ON auf Basis von Civey-Umfragen, 2023'
            },
            seoTitle: 'Zukunftsindex - durch Verbrauchswende',
            stories: storyGroups.zukunftsindex,
            meta: {
              regionFilters: null,
              tableHead: { region: { 0: ['Region', 'Platz', 'Anteil Verbrauchswende'] } },
              data: { category: 'consumptionChange', categoryTooltip: 'consumptionChange', page: 'futureIndex', isTotal: true },
              api: { tooltip: '/futureIndex/tooltip', ranksStates: '/futureIndex/rank/states/consumptionChange' }
            }
          },
          {
            key: 'durch-verbrauchswende-heiztemperatur-senken',
            displayname: 'Heiztemperatur senken',
            headline: 'Verbrauchswende',
            content: 'Welche CO₂-Einsparpotenziale sind konkret durch das Senken der Raumtemperatur möglich, wenn die Menschen in Deutschland ihre kurzfristigen Pläne umsetzen?',
            additional_info: {
              sourcesHref: 'https://www.eon.de/de/unternehmen/presse/energieatlas/quellen.html#zukunftsindex',
              text: 'Berechnung von Energy Brainpool im Auftrag von E.ON auf Basis von Civey-Umfragen, 2023'
            },
            seoTitle: 'Zukunftsindex - durch Verbrauchswende',
            stories: storyGroups.zukunftsindex,
            meta: {
              regionFilters: null,
              tableHead: { region: { 0: ['Region', 'Platz', 'Heiztemperatur senken'] } },
              data: { category: 'consumptionChange/lowerHeating', categoryTooltip: 'consumptionChange', page: 'futureIndex' },
              api: { tooltip: '/futureIndex/tooltip', ranksStates: '/futureIndex/rank/states/consumptionChange/lowerHeating' }
            }
          },
          {
            key: 'durch-verbrauchswende-smarte-thermostate',
            displayname: 'Smarte Thermostate',
            headline: 'Verbrauchswende',
            content: 'Welche CO₂-Einsparpotenziale sind konkret durch die Nutzung smarter Thermostate möglich, wenn die Menschen in Deutschland ihre kurzfristigen Pläne umsetzen?',
            additional_info: {
              sourcesHref: 'https://www.eon.de/de/unternehmen/presse/energieatlas/quellen.html#zukunftsindex',
              text: 'Berechnung von Energy Brainpool im Auftrag von E.ON auf Basis von Civey-Umfragen, 2023'
            },
            seoTitle: 'Zukunftsindex - durch Verbrauchswende',
            stories: storyGroups.zukunftsindex,
            meta: {
              regionFilters: null,
              tableHead: { region: { 0: ['Region', 'Platz', 'Smarte Thermostate'] } },
              data: { category: 'consumptionChange/smartThermostat', categoryTooltip: 'consumptionChange', page: 'futureIndex' },
              api: { tooltip: '/futureIndex/tooltip', ranksStates: '/futureIndex/rank/states/consumptionChange/smartThermostat' }
            }
          }
        ]
      }
    ]
  },
  {
    key: 'e-mobility',
    categories: [
      {
        key: 'e-auto-besitzer',
        displayname: 'Elektroautos',
        headline: 'E-Autos: Verbreitung in Deutschland',
        content: 'In welchen Bundesländern sind die meisten vollelektrischen Autos zugelassen? Der Überblick.',
        additional_info: {
          sourcesHref: 'https://www.eon.de/de/unternehmen/presse/energieatlas/quellen.html#emobility',
          text: 'Kraftfahrtbundesamt, {{month}} {{year}}',
          monthOverrides: { default: 'Januar', 2025: 'April', 2026: 'Oktober' },
          yearOverrides: { 2026: '2025' }
        },
        notes: ['* E-Autos geladen mit 100% Ökostrom'],
        subcategories: [],
        seoTitle: 'E-Auto-Besitzer',
        stories: storyGroups.eMobilityVehicles,
        meta: {
          regionFilters: [0, 1],
          tableHead: {
            region: {
              0: ['Region', 'Platz', 'Elektroautos', 'CO₂-Einsparung/Jahr*'],
              1: ['Region', 'Platz', 'Elektroautos', 'CO₂-Einsparung/Jahr*']
            }
          },
          tableHead_2025: {
            region: {
              0: ['Region', 'Platz', 'Anteil Elektroautos', 'Elektroautos', 'CO₂-Einsparung/Jahr*'],
              1: ['Region', 'Platz', 'Anteil Elektroautos', 'Elektroautos', 'CO₂-Einsparung/Jahr*']
            }
          },
          data: { page: 'electricVehicles', legendStyle: 'vehicles' },
          api: { tooltip: '/electricVehicles/tooltip', ranksStates: '/electricVehicles/rank/states', ranksLandkreise: '/electricVehicles/rank/districts' }
        }
      },
      {
        key: 'ladestationen',
        displayname: 'Lademöglichkeiten',
        headline: 'E-Autos: Ladeleistung in Deutschland',
        content: 'Wie viel Ladeleistung für Elektroautos gibt es in Deutschland? Und wo gibt es die meisten Lademöglichkeiten? Der Überblick.',
        additional_info: {
          sourcesHref: 'https://www.eon.de/de/unternehmen/presse/energieatlas/quellen.html#emobility',
          text: 'Bundesnetzagentur, {{month}} {{year}}',
          monthOverrides: { default: 'März', 2021: 'August', 2022: 'August', 2023: 'August', 2025: 'Mai', 2026: 'Januar' },
          yearOverrides: { 2021: '2023', 2022: '2023', 2023: '2023' }
        },
        subcategories: [],
        seoTitle: 'E-Mobility - Lademöglichkeiten',
        stories: storyGroups.eMobilityVehicles,
        meta: {
          regionFilters: [0, 1],
          tableHead: { region: { 0: ['Region', 'Platz', 'Ladepunkte', 'Gesamte Ladeleistung', 'Ladeleistung pro E-Auto'] } },
          data: { page: 'chargers', legendStyle: 'charging', markerIcon: 'ladestation' },
          api: { tooltip: '/chargers/tooltip', ranksStates: '/chargers/rank/states', ranksLandkreise: '/chargers/rank/districts', geosRegion2: '/data/ladestationen.json' }
        }
      },
      {
        key: 'e-lkw',
        displayname: 'Elektro-Lkw',
        headline: 'E-Lkw: Verbreitung in Deutschland',
        content: 'In welchen Bundesländern sind die meisten elektrischen Lkw und Transporter zugelassen? Der Überblick.',
        additional_info: {
          sourcesHref: 'https://www.eon.de/de/unternehmen/presse/energieatlas/quellen.html#emobility',
          text: 'Kraftfahrtbundesamt, Oktober {{year}}',
          yearOverrides: { 2026: '2025' }
        },
        notes: ['* E-Lkw geladen mit 100% Ökostrom'],
        subcategories: [],
        seoTitle: 'Elektro-Lkw',
        stories: storyGroups.eLkw,
        meta: {
          regionFilters: null,
          tableHead: { region: { 0: ['Region', 'Platz', 'E-Lkw', 'CO₂-Einsparung/Jahr*'] } },
          data: { page: 'electricTrucks', legendStyle: 'vehicles' },
          api: { tooltip: '/electricTrucks/tooltip', ranksStates: '/electricTrucks/rank/states' }
        }
      },
      {
        key: 'bidi-atlas',
        displayname: 'Bidi. Potenzial',
        headline: 'Bidirektionales Laden in Deutschland',
        content: 'In welchen Bundesländern gibt es die meisten bidi-ready Fahrzeuge? Und welches Speicherpotenzial geht damit einher? Der Überblick.',
        additional_info: {
          sourcesHref: 'https://www.eon.de/de/unternehmen/presse/energieatlas/quellen.html#emobility',
          text: 'KBA, ADAC, eigene Berechnungen, {{year}}'
        },
        subcategories: [],
        seoTitle: 'Bidi. Potenzial',
        stories: storyGroups.bidiAtlas,
        meta: {
          regionFilters: null,
          tableHead: { region: { 0: ['Region', 'Platz', 'Bidi-ready E-Autos', 'Versorgbare Haushalte'] } },
          data: { page: 'bidiAtlas', legendStyle: 'vehicles' },
          api: { tooltip: '/bidiAtlas/tooltip', ranksStates: '/bidiAtlas/rank/states' }
        }
      },
      {
        key: 'e-bikes',
        displayname: 'E-Bikes',
        headline: 'E-Bikes: Verbreitung in Deutschland',
        content: 'In welchen Regionen sind E-Bikes besonders beliebt? Alle Infos unter anderem zum E-Bike Besitz im Überblick.',
        additional_info: {
          sourcesHref: 'https://www.eon.de/de/unternehmen/presse/energieatlas/quellen.html#emobility',
          text: 'Civey-Umfrage im Auftrag von E.ON, repräsentativ für E-Bike-Besitzer, {{year}}'
        },
        subcategories: [],
        seoTitle: 'E-Bike',
        stories: storyGroups.eBikes,
        meta: {
          regionFilters: [0, 1],
          tableHead: {
            region: {
              0: ['Region', 'Platz', 'E-Bike-Besitzer', 'Laden mit Ökostrom'],
              1: ['Region', 'Platz', 'E-Bike-Besitzer']
            }
          },
          tableHead_2023: {
            region: {
              0: ['Region', 'Platz', 'E-Bike-Besitzer'],
              1: ['Region', 'Platz', 'E-Bike-Besitzer']
            }
          },
          tableHead_2025: {
            region: {
              0: ['Region', 'Platz', 'E-Bike-Besitzer', 'Flex-Bereitschaft'],
              1: ['Region', 'Platz', 'E-Bike-Besitzer']
            }
          },
          data: { page: 'electricBicycles', legendUnit: '%' },
          api: { tooltip: '/electricBicycles/tooltip', ranksStates: '/electricBicycles/rank/states', ranksLandkreise: '/electricBicycles/rank/districts' }
        }
      }
    ]
  }
];

const isLeaf = (node) => {
  return !node.subcategories || node.subcategories.length === 0;
};

const toFlatPage = (node, path, tab) => {
  if (!node.meta) return null;
  return {
    path,
    tab,
    displayname: node.displayname,
    headline: node.headline,
    content: node.content,
    additional_info: node.additional_info,
    notes: node.notes,
    seoTitle: node.seoTitle,
    stories: node.stories,
    meta: node.meta,
  };
};

const compilePages = (tabs) => {
  const pages = [];

  tabs.forEach((tab) => {
    tab.categories.forEach((category) => {
      const categoryPath = `/${tab.key}/${category.key}`;

      if (isLeaf(category)) {
        const page = toFlatPage(category, categoryPath, tab.key);
        if (page) pages.push(page);
        return;
      }

      category.subcategories.forEach((subcategory) => {
        const page = toFlatPage(subcategory, `${categoryPath}/${subcategory.key}`, tab.key);
        if (page) pages.push(page);
      });
    });
  });

  return pages;
};

const derivePathUrlsKey = (key) => {
  return key.toUpperCase().replace(/-/g, '_');
};

const buildPathUrls = (tabs) => {
  const result = {};

  tabs.forEach((tab) => {
    const tabPathUrlsKey = derivePathUrlsKey(tab.key);
    result[`${tabPathUrlsKey}_PART`] = `/${tab.key}`;
    result[tabPathUrlsKey] = {};

    tab.categories.forEach((category) => {
      const categoryPath = `/${tab.key}/${category.key}`;
      const categoryPathUrlsKey = derivePathUrlsKey(category.key);

      if (isLeaf(category)) {
        result[tabPathUrlsKey][categoryPathUrlsKey] = categoryPath;
        return;
      }

      result[tabPathUrlsKey][`${categoryPathUrlsKey}_PART`] = categoryPath;
      const subcategoryPaths = {};
      category.subcategories.forEach((subcategory) => {
        subcategoryPaths[derivePathUrlsKey(subcategory.key)] = `${categoryPath}/${subcategory.key}`;
      });
      result[tabPathUrlsKey][categoryPathUrlsKey] = subcategoryPaths;
    });
  });

  return result;
};

const pages = compilePages(tree);
const PATH_URLS = buildPathUrls(tree);

module.exports = { tree, pages, PATH_URLS };
